import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Ticket, Users, DollarSign, QrCode, Plus, X, Trash2,
  TrendingUp, BarChart3, Check, Clock, Pencil, Download,
} from "lucide-react";

import { toast } from "sonner";

export default function TicketManagePage() {
  const { eventId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stats");
  const [creating, setCreating] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", price: "", quantity: "", description: "" });
  const [createLoading, setCreateLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [salesRes, attendeesRes] = await Promise.all([
        api.get(`/tickets/events/${eventId}/sales`),
        api.get(`/tickets/events/${eventId}/attendees`),
      ]);
      setEvent(salesRes.data.event);
      setCategories(salesRes.data.categories);
      setSales(salesRes.data.sales);
      setStats(salesRes.data.stats);
      setAttendees(attendeesRes.data.attendees);
    } catch {
      navigate("/dashboard");
    }
    setLoading(false);
  }, [api, eventId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateCategory = async () => {
    if (!newCat.name.trim() || !newCat.quantity) return;
    setCreateLoading(true);
    try {
      await api.post(`/tickets/events/${eventId}/categories`, {
        name: newCat.name.trim(),
        price: Math.round(parseFloat(newCat.price || "0") * 100),
        quantity: parseInt(newCat.quantity),
        description: newCat.description,
      });
      setNewCat({ name: "", price: "", quantity: "", description: "" });
      setCreating(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setCreateLoading(false);
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm("Supprimer cette categorie ?")) return;
    try {
      await api.delete(`/tickets/categories/${catId}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await api.get(`/tickets/events/${eventId}/export`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `billets_${event?.title?.replace(/[^a-z0-9]/gi, "_").slice(0, 40) || "event"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Liste exportee !");
    } catch (err) {
      toast.error("Erreur lors de l'export");
    }
  };

  if (loading) return <div className="py-6"><div className="bg-card rounded-3xl h-48 animate-pulse" /></div>;
  if (!event) return null;

  return (
    <div className="py-6 space-y-6" data-testid="ticket-manage-page">
      <button onClick={() => navigate(`/events/${eventId}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Retour a l'evenement
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-foreground flex items-center gap-2">
            <Ticket className="w-7 h-7 text-[#FF6B35]" />
            Billetterie
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{event.title}</p>
        </div>
        <button
          onClick={() => navigate(`/events/${eventId}/scanner`)}
          className="bg-[#FF6B35] text-white font-bold px-4 py-2.5 rounded-full text-sm flex items-center gap-1.5 hover:scale-105 active:scale-95 transition-transform"
          data-testid="open-scanner-btn"
        >
          <QrCode className="w-4 h-4" />
          Scanner
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-3" data-testid="ticket-stats">
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div>
                <p className="font-heading font-extrabold text-xl text-foreground">{stats.total_sold}</p>
                <p className="text-[10px] text-muted-foreground">Vendus / {stats.total_capacity}</p>
              </div>
            </div>
            <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-[#FF6B35] rounded-full" style={{ width: `${stats.fill_rate}%` }} />
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="font-heading font-extrabold text-xl text-foreground">{(stats.artist_revenue / 100).toFixed(0)} EUR</p>
                <p className="text-[10px] text-muted-foreground">Revenus nets</p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Commission ReallyFan: {(stats.platform_fees / 100).toFixed(0)} EUR
            </p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[#7000FF]/10 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#7000FF]" />
              </div>
              <div>
                <p className="font-heading font-extrabold text-xl text-foreground">{stats.scanned}</p>
                <p className="text-[10px] text-muted-foreground">Scannes</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="font-heading font-extrabold text-xl text-foreground">{stats.fill_rate}%</p>
                <p className="text-[10px] text-muted-foreground">Remplissage</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-card rounded-full p-1 border border-border">
        {[
          { key: "stats", icon: BarChart3, label: "Categories" },
          { key: "sales", icon: DollarSign, label: "Ventes" },
          { key: "attendees", icon: Users, label: "Acheteurs" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-medium transition-all ${
              tab === t.key ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
            }`}
            data-testid={`ticket-tab-${t.key}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Categories tab */}
      {tab === "stats" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Categories de billets</p>
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-1 text-xs text-[#FF6B35] hover:underline"
              data-testid="add-category-btn"
            >
              <Plus className="w-3 h-3" /> Ajouter
            </button>
          </div>

          {creating && (
            <div className="bg-card border border-[#FF6B35]/30 rounded-2xl p-4 space-y-3" data-testid="new-category-form">
              <Input
                placeholder="Nom (ex: Fosse, Tribune, VIP)"
                value={newCat.name}
                onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                data-testid="cat-name-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="number"
                  placeholder="Prix (EUR, 0=gratuit)"
                  value={newCat.price}
                  onChange={(e) => setNewCat({ ...newCat, price: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="cat-price-input"
                />
                <Input
                  type="number"
                  placeholder="Quantite"
                  value={newCat.quantity}
                  onChange={(e) => setNewCat({ ...newCat, quantity: e.target.value })}
                  className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                  data-testid="cat-quantity-input"
                />
              </div>
              <Input
                placeholder="Description (optionnel)"
                value={newCat.description || ""}
                onChange={(e) => setNewCat({ ...newCat, description: e.target.value })}
                className="bg-background border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10"
                data-testid="cat-description-input"
              />

              {/* Frais plateforme */}
              <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl p-3 text-xs text-foreground">
                <p className="font-bold text-[#FF6B35] mb-1">Frais plateforme : 1 EUR par billet vendu</p>
                <p className="text-muted-foreground text-[11px]">
                  Ces frais sont preleves automatiquement et ajoutes au prix paye par l'acheteur (tu touches 100% du prix de ton billet).
                  {newCat.price && parseFloat(newCat.price) > 0 && (
                    <span className="block mt-1 text-foreground">
                      <strong>L'acheteur paiera : {(parseFloat(newCat.price) + 1).toFixed(2)} EUR</strong> par billet.
                    </span>
                  )}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleCreateCategory}
                  disabled={createLoading || !newCat.name.trim() || !newCat.quantity}
                  className="bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-xs disabled:opacity-50"
                  data-testid="cat-create-btn"
                >
                  {createLoading ? "..." : "Creer"}
                </button>
                <button
                  onClick={() => setCreating(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {categories.map((cat) => (
            <div key={cat.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`category-${cat.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-bold text-sm text-foreground">{cat.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {cat.price === 0 ? "Gratuit" : `${(cat.price / 100).toFixed(2)} EUR`}
                    {" + 1 EUR frais"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-bold text-foreground">{cat.sold}/{cat.quantity}</p>
                    <p className="text-[10px] text-muted-foreground">vendus</p>
                  </div>
                  {cat.sold === 0 && (
                    <button
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#FF6B35] rounded-full transition-all"
                  style={{ width: `${cat.quantity > 0 ? (cat.sold / cat.quantity * 100) : 0}%` }}
                />
              </div>
            </div>
          ))}
          {categories.length === 0 && !creating && (
            <p className="text-center py-8 text-sm text-muted-foreground">Aucune categorie. Cree-en une pour commencer a vendre !</p>
          )}
        </div>
      )}

      {/* Sales tab */}
      {tab === "sales" && (
        <div className="space-y-2">
          {sales.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">Aucune vente pour le moment</p>
          ) : (
            sales.map((s) => (
              <div key={s.id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3" data-testid={`sale-${s.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#FF6B35]/10 flex items-center justify-center shrink-0">
                  <Ticket className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{s.buyer_pseudo}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {s.quantity} billet{s.quantity > 1 ? "s" : ""} - {new Date(s.created_at).toLocaleString("fr-FR")}
                  </p>
                </div>
                <p className="text-sm font-bold text-foreground shrink-0">{(s.total / 100).toFixed(2)} EUR</p>
              </div>
            ))
          )}
        </div>
      )}

      {/* Attendees tab */}
      {tab === "attendees" && (
        <div className="space-y-2">
          {attendees.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{attendees.length} billet{attendees.length > 1 ? "s" : ""}</p>
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 bg-[#FF6B35] text-white font-bold px-3 py-1.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform"
                data-testid="export-csv-btn"
              >
                <Download className="w-3.5 h-3.5" /> Exporter CSV
              </button>
            </div>
          )}
          {attendees.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">Aucun acheteur</p>
          ) : (
            attendees.map((t) => (
              <div key={t.id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3" data-testid={`attendee-ticket-${t.id}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  t.status === "used" ? "bg-emerald-500/10" : "bg-[#FF6B35]/10"
                }`}>
                  {t.status === "used" ? <Check className="w-4 h-4 text-emerald-500" /> : <Ticket className="w-4 h-4 text-[#FF6B35]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{t.buyer_pseudo}</p>
                  <p className="text-[10px] text-muted-foreground">{t.category_name}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  t.status === "used"
                    ? "bg-emerald-500/10 text-emerald-500"
                    : "bg-[#FF6B35]/10 text-[#FF6B35]"
                }`}>
                  {t.status === "used" ? "Scanne" : "Valide"}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
